// @ts-check
import { isAuthHomeProjection, validateAuthBootSlices } from '../auth-home-projections.js';
import { homeCalendarQueryString } from '../home-calendar-contract.js';
export { homeCalendarRequestScope, assertHomeCalendarResponseScope, isHomeCalendarResponseCurrent } from '../home-calendar-contract.js';
import { readUserIdentifier } from '../user-requests.js';
export { readGoogleTokenRequest, readAppleTokenRequest } from '../auth-provider-requests.js';
import { hasAuthProfileFields as hasProfileFields, isAuthVerificationRequest } from '../auth-profile-projections.js';
import { readAuthRecoveryRequest, readAuthResetPasswordRequest, readAuthChangePasswordRequest, readAuthResetTokenRequest, readAuthEmailCodeRequest, readAuthEmailResendRequest, readAuthProfileUpdateRequest, readAuthVerificationStartRequest, readAuthIdentityPathRequest } from '../auth-account-requests.js';
export { readAuthRecoveryRequest, readAuthResetPasswordRequest, readAuthChangePasswordRequest, readAuthResetTokenRequest, readAuthEmailCodeRequest, readAuthEmailResendRequest, readAuthProfileUpdateRequest, readAuthVerificationStartRequest, readAuthVerificationListQuery, readAuthVerificationReviewRequest, readAuthIdentityPathRequest } from '../auth-account-requests.js';
export {AuthRequestContractError, readMemberRegistrationRequest, readMemberVerificationRequest, readPasswordLoginRequest, readRefreshTokenRequest} from '../auth-requests.js';
// Shared auth domain client (canonical /api/auth/* endpoints).
//
// Auth is unusual: each app targets a configured auth base (the gateway in prod
// via Vercel rewrites, or an origin) rather than the standard apiClient base.
// Web prepends a base that already ends in `/api/auth`, so it consumes the
// SUFFIX form (`/login`); the native apps build full paths against an origin, so
// they use `authApiPath()` to get the `/api/auth/...` form. This module owns the
// path strings only — each app keeps its own base resolution, transport, token
// handling, response shaping, and WebAuthn encoding.
//
// Brand-specialty routes (kali/nuri/nuri-teacher/guardian) live in
// ./auth-specialty.js.
//
// createAuthClient's adapter mirrors the createApiClient surface over SUFFIX
// paths: get(path, options), post(path, body, options), put(path, body, options),
// delete(path, options). The adapter is responsible for prepending the app's
// auth base.

/**
 * @typedef {object} AuthAdapter
 * @property {(path: string, options?: unknown) => Promise<unknown>} get
 * @property {(path: string, body?: unknown, options?: unknown) => Promise<unknown>} post
 * @property {(path: string, body?: unknown, options?: unknown) => Promise<unknown>} put
 * @property {(path: string, options?: unknown) => Promise<unknown>} delete
 */

/**
 * @typedef {object} AuthClient
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} login
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} register
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} verify
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} verifyEmail
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} resendEmailVerification
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} refresh
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} google
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} apple
 * @property {(options?: unknown) => Promise<unknown>} getMe
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} updateMe
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} forgotPassword
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} requestBahaiIdVerification
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} resetPassword
 * @property {(body: unknown, options?: unknown) => Promise<unknown>} changePassword
 * @property {(token: string, options?: unknown) => Promise<unknown>} validateResetToken
 * @property {(userId: string, options?: unknown) => Promise<unknown>} deleteUser
 * @property {(communityId: string, options?: unknown) => Promise<unknown>} getHomeOverview
 */

export const AUTH_BASE_PATH = '/api/auth';

// Suffixes relative to the configured auth base (which already ends /api/auth).
export const AUTH_PATHS = {
  login: '/login',
  register: '/register',
  verify: '/verify',
  refresh: '/refresh',
  me: '/me',
  google: '/google',
  apple: '/apple',
  verifyEmail: '/verify-email',
  verifyEmailResend: '/verify-email/resend',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  changePassword: '/change-password',
  forgotBahaiId: '/forgot-bahai-id',
  requestBahaiIdVerification: '/request-bahai-id-verification',
};

export const AUTH_PASSKEY_PATHS = {
  credentials: '/passkey/credentials',
  authenticateOptions: '/passkey/authenticate/options',
  authenticateVerify: '/passkey/authenticate/verify',
  registerOptions: '/passkey/register/options',
  registerVerify: '/passkey/register/verify',
};

const enc = (value) => encodeURIComponent(value ?? '');

export const buildValidateResetTokenPath = (token) =>
  `/validate-reset-token/${enc(token)}`;
export const buildDeleteUserPath = (userId) => `/delete-user/${enc(userId)}`;
export const buildHomeOverviewPath = (communityId, query = {}) => `/homeOverview/${enc(communityId)}${homeCalendarQueryString(query)}`;
export const buildHomeSummaryPath = (communityId, query = {}) => `/homeSummary/${enc(communityId)}${homeCalendarQueryString(query)}`;
export const buildHomeBootPath = (query = {}) => `/api/home/boot${homeCalendarQueryString(query)}`;
export const buildBahaiIdVerificationPath = (requestId) =>
  `/bahai-id-verification/${enc(requestId)}`;
export const buildBahaiIdVerificationApiPath = (requestId) =>
  requestId
    ? authApiPath(buildBahaiIdVerificationPath(requestId))
    : authApiPath('/bahai-id-verification');
export const buildPasskeyCredentialPath = (credentialId) =>
  `${AUTH_PASSKEY_PATHS.credentials}/${enc(credentialId)}`;

// Turn a suffix (or already-full path) into the full `/api/auth/...` path used by
// the native apps, which target an origin base. Passing a full path is a no-op.
export const authApiPath = (suffix) => {
  const value = String(suffix ?? '');
  if (value.startsWith(AUTH_BASE_PATH)) {
    return value;
  }
  return `${AUTH_BASE_PATH}${value.startsWith('/') ? value : `/${value}`}`;
};

export class AuthResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'AuthResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const hasId = (value, key) => isRecord(value) && typeof value[key] === 'string' && value[key].length > 0;


// The main backend returns `id` on reads and `_id` on writes. The auth gateway
// keeps `id` on reads and includes both identifiers on writes. Preserve those
// wire shapes while rejecting a missing identity or malformed profile fields.
export function readAuthMeResponse(payload) {
  if (!hasId(payload, 'id') || !hasProfileFields(payload)) {
    throw new AuthResponseContractError('auth me', 'expected a direct profile with id.');
  }
  return payload;
}

export function readAuthProfileUpdateResponse(payload) {
  if (!hasId(payload, '_id') || !hasProfileFields(payload)) {
    throw new AuthResponseContractError('auth profile update', 'expected a direct profile with _id.');
  }
  return payload;
}

export function readAuthActionResponse(payload) {
  if (!isRecord(payload) || typeof payload.message !== 'string' || !payload.message) {
    throw new AuthResponseContractError('auth action', 'expected a message acknowledgement.');
  }
  return payload;
}

export function readAuthResetTokenResponse(payload) {
  if (!isRecord(payload) || payload.success !== true || typeof payload.message !== 'string' || !payload.message) {
    throw new AuthResponseContractError('reset token validation', 'expected a successful token decision.');
  }
  return payload;
}

const isToken = value => typeof value === 'string' && value.length > 0;

export function readAuthSessionResponse(payload) {
  if (!isRecord(payload) || !isToken(payload.token) || !isToken(payload.refreshToken)
    || !isRecord(payload.user) || !hasId(payload.user, 'id') || !hasProfileFields(payload.user)
    || (payload.accessToken !== undefined && payload.accessToken !== payload.token)
    || (payload.newRefreshToken !== undefined && payload.newRefreshToken !== payload.refreshToken)
    || (payload.accessTokenExpiresIn !== undefined && (!Number.isFinite(payload.accessTokenExpiresIn) || payload.accessTokenExpiresIn <= 0))
    || (payload.refreshTokenExpiresIn !== undefined && (!Number.isFinite(payload.refreshTokenExpiresIn) || payload.refreshTokenExpiresIn <= 0))) {
    throw new AuthResponseContractError('auth session', 'expected tokens and a direct user identity.');
  }
  return payload;
}

export function readAuthLoginResponse(payload) {
  if (isRecord(payload) && payload.provider === 'liquid-spirit'
    && isToken(payload.authorizeUrl) && isToken(payload.state) && isToken(payload.nonce)) {
    return payload;
  }
  if (isRecord(payload) && (payload.provider === 'google' || payload.provider === 'apple')
    && isToken(payload.signInUrl) && isToken(payload.state)) {
    return payload;
  }
  return readAuthSessionResponse(payload);
}

export function readAuthRegisterResponse(payload) {
  if (isRecord(payload) && typeof payload.message === 'string' && payload.message) {
    return payload;
  }
  if (isRecord(payload) && payload.provider === 'liquid-spirit'
    && isToken(payload.authorizeUrl) && isToken(payload.state) && isToken(payload.nonce)) {
    return payload;
  }
  if (isRecord(payload) && (payload.provider === 'google' || payload.provider === 'apple')
    && isToken(payload.signInUrl) && isToken(payload.state)) {
    return payload;
  }
  throw new AuthResponseContractError('auth registration', 'expected a verification acknowledgement or provider authorization response.');
}

export function readAuthVerificationCompletionResponse(payload) {
  const userId = payload?.user?.id || payload?.user?._id;
  if (!isRecord(payload) || payload.success !== true || typeof payload.message !== 'string' || !payload.message
    || !isToken(payload.token) || !isRecord(payload.user) || typeof userId !== 'string' || !userId || !hasProfileFields(payload.user)
    || (payload.refreshToken !== undefined && !isToken(payload.refreshToken))) {
    throw new AuthResponseContractError('auth verification completion', 'expected a completed account, user identity, and access token.');
  }
  return payload;
}

export function readAuthProviderResponse(payload) {
  if (isRecord(payload) && (payload.provider === 'liquid-spirit'
      || payload.provider === 'google' || payload.provider === 'apple')) {
    return readAuthLoginResponse(payload);
  }
  const userId = payload?.user?.id || payload?.user?._id;
  if (!isRecord(payload) || !isToken(payload.token) || !isRecord(payload.user)
    || typeof userId !== 'string' || !userId || !hasProfileFields(payload.user)
    || (payload.refreshToken !== undefined && !isToken(payload.refreshToken))) {
    throw new AuthResponseContractError('auth provider sign-in', 'expected a provider authorization start or user session.');
  }
  return payload;
}

export function readAuthRegisterNewUserGoneResponse(payload) {
  if (!isRecord(payload) || typeof payload.message !== 'string'
    || !payload.message.startsWith('registerNewUser is retired.')) {
    throw new AuthResponseContractError('retired registerNewUser route', 'expected the production retirement message.');
  }
  return payload;
}

export function readAuthAppleCallbackHtmlResponse(payload) {
  if (typeof payload !== 'string' || !payload.startsWith('<!DOCTYPE html>')
    || !payload.includes('Apple Sign-In') || !payload.includes('postMessage(payload')) {
    throw new AuthResponseContractError('Apple OAuth callback', 'expected the HTML window bridge response.');
  }
  return payload;
}

export function readAuthTokenIntrospectionResponse(payload) {
  if (!isRecord(payload) || typeof payload.success !== 'boolean') {
    throw new AuthResponseContractError('token introspection', 'expected a successful result or a structured token error.');
  }

  if (payload.success === false) {
    if (typeof payload.message !== 'string' || !payload.message
      || (payload.verifiedWith !== undefined && payload.verifiedWith !== null && typeof payload.verifiedWith !== 'string')) {
      throw new AuthResponseContractError('token introspection', 'expected a successful result or a structured token error.');
    }
    return payload;
  }

  const claims = payload.claims;
  if (typeof payload.verifiedWith !== 'string' || !payload.verifiedWith
    || typeof payload.principalId !== 'string' || !payload.principalId || !isRecord(claims)) {
    throw new AuthResponseContractError('token introspection', 'expected verification metadata, principal, and claims.');
  }
  return payload;
}

const hasVerifiedAt = value => typeof value === 'string' && Number.isFinite(Date.parse(value));

export function readAuthEmailVerificationResponse(payload) {
  if (!isRecord(payload) || typeof payload.message !== 'string' || !payload.message
    || !hasVerifiedAt(payload.emailVerifiedAt)
    || (payload.alreadyVerified !== undefined && payload.alreadyVerified !== true)) {
    throw new AuthResponseContractError('email verification', 'expected a verification timestamp and message.');
  }
  return payload;
}

export function readAuthEmailVerificationResendResponse(payload) {
  if (!isRecord(payload) || typeof payload.message !== 'string' || !payload.message
    || (payload.alreadyVerified === undefined
      ? payload.emailVerifiedAt !== undefined
      : payload.alreadyVerified !== true || !hasVerifiedAt(payload.emailVerifiedAt))) {
    throw new AuthResponseContractError('email verification resend', 'expected a sent or already-verified acknowledgement.');
  }
  return payload;
}

export function readAuthAppleGetResponse(payload) {
  if (!isRecord(payload) || payload.message !== 'Use POST to authenticate with Apple.') {
    throw new AuthResponseContractError('Apple GET deprecation', 'expected the terminal 405 message.');
  }
  return payload;
}

const isVerificationRequest = isAuthVerificationRequest;

export function readBahaiIdVerificationRequestsResponse(payload) {
  if (!isRecord(payload) || !Array.isArray(payload.requests) || !payload.requests.every(isVerificationRequest)) {
    throw new AuthResponseContractError('Bahá\u2019í ID verification requests', 'expected a requests array of verification records.');
  }
  return payload;
}

export function readBahaiIdVerificationReviewResponse(payload) {
  if (!isRecord(payload) || typeof payload.message !== 'string' || !payload.message || !isVerificationRequest(payload.request)) {
    throw new AuthResponseContractError('Bahá\u2019í ID verification review', 'expected a message and reviewed verification record.');
  }
  return payload;
}

export function readAuthHomeSummaryResponse(payload) {
  if (!isRecord(payload) || payload.success !== true || payload.source !== 'homeSummary'
    || !isAuthHomeProjection(payload)) {
    throw new AuthResponseContractError('home summary', 'expected the complete home summary envelope.');
  }
  return payload;
}

export function readAuthHomeOverviewResponse(payload) {
  if (!isRecord(payload) || payload.success !== true || !isAuthHomeProjection(payload)) {
    throw new AuthResponseContractError('home overview', 'expected the complete home overview envelope.');
  }
  return payload;
}

export function readAuthHomeBootResponse(payload) {
  const sliceNames = ['notifications', 'household', 'venues', 'treasuryFunds'];
  const unavailable = payload?.unavailable;
  if (!isRecord(payload) || payload.success !== true
    || typeof payload.generatedAt !== 'string' || Number.isNaN(Date.parse(payload.generatedAt))
    || !Array.isArray(unavailable) || unavailable.some(name => !sliceNames.includes(name))
    || !Array.isArray(payload.notifications) || !isRecord(payload.household) && payload.household !== null
    || !Array.isArray(payload.venues) || !Array.isArray(payload.treasuryFunds)) {
    throw new AuthResponseContractError('home boot', 'expected the home overview and all named slices with availability metadata.');
  }
  readAuthHomeOverviewResponse(payload.home);
  try { validateAuthBootSlices(payload); } catch {
    throw new AuthResponseContractError('home boot', 'invalid notification, household, venue or fund slice.');
  }
  return payload;
}

/**
 * @param {AuthAdapter} adapter
 * @returns {AuthClient}
 */
export const createAuthClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createAuthClient requires a transport adapter.');
  }

  return {
    login(body, options = {}) {
      return adapter.post(AUTH_PATHS.login, body, options).then(readAuthLoginResponse);
    },
    register(body, options = {}) {
      return adapter.post(AUTH_PATHS.register, body, options).then(readAuthRegisterResponse);
    },
    verify(body, options = {}) {
      return adapter.post(AUTH_PATHS.verify, body, options).then(readAuthVerificationCompletionResponse);
    },
    // Email-ownership verification: confirm the emailed 6-digit code, or ask
    // for a fresh one. Both act on the signed-in user (Authorization required).
    verifyEmail(body, options = {}) {
      readAuthEmailCodeRequest(body);
      return adapter.post(AUTH_PATHS.verifyEmail, body, options).then(readAuthEmailVerificationResponse);
    },
    resendEmailVerification(body, options = {}) {
      readAuthEmailResendRequest(body);
      return adapter.post(AUTH_PATHS.verifyEmailResend, body, options).then(readAuthEmailVerificationResendResponse);
    },
    refresh(body, options = {}) {
      return adapter.post(AUTH_PATHS.refresh, body, options).then(readAuthSessionResponse);
    },
    google(body, options = {}) {
      return adapter.post(AUTH_PATHS.google, body, options).then(readAuthProviderResponse);
    },
    apple(body, options = {}) {
      return adapter.post(AUTH_PATHS.apple, body, options).then(readAuthProviderResponse);
    },
    getMe(options = {}) {
      return adapter.get(AUTH_PATHS.me, options).then(readAuthMeResponse);
    },
    updateMe(body, options = {}) {
      readAuthProfileUpdateRequest(body);
      return adapter.put(AUTH_PATHS.me, body, options).then(readAuthProfileUpdateResponse);
    },
    forgotPassword(body, options = {}) {
      readAuthRecoveryRequest(body);
      return adapter.post(AUTH_PATHS.forgotPassword, body, options).then(readAuthActionResponse);
    },
    requestBahaiIdVerification(body, options = {}) {
      readAuthVerificationStartRequest(body);
      return adapter.post(AUTH_PATHS.requestBahaiIdVerification, body, options).then(readAuthActionResponse);
    },
    resetPassword(body, options = {}) {
      readAuthResetPasswordRequest(body);
      return adapter.post(AUTH_PATHS.resetPassword, body, options).then(readAuthActionResponse);
    },
    changePassword(body, options = {}) {
      readAuthChangePasswordRequest(body);
      return adapter.post(AUTH_PATHS.changePassword, body, options).then(readAuthActionResponse);
    },
    validateResetToken(token, options = {}) {
      readAuthResetTokenRequest(token);
      return adapter.get(buildValidateResetTokenPath(token), options).then(readAuthResetTokenResponse);
    },
    deleteUser(userId, options = {}) {
      readAuthIdentityPathRequest(userId);
      return adapter.delete(buildDeleteUserPath(userId), options).then(readAuthActionResponse);
    },
    getHomeOverview(communityId, options = {}) {
      readUserIdentifier(communityId);
      return adapter.get(buildHomeOverviewPath(communityId), options).then(readAuthHomeOverviewResponse);
    },
  };
};
